import React, { useState } from 'react';
import { X, Mail, Users, CircleAlert as AlertCircle, Loader as Loader2 } from 'lucide-react';
import { Button } from '../ui/Button';
import type { WorkerContact } from '../../types/orderIssues';

interface SendIssueEmailModalProps {
  issueCount: number;
  contacts: WorkerContact[];
  defaultRecipientIds: string[];
  onConfirm: (recipientIds: string[]) => Promise<void>;
  onClose: () => void;
}

export const SendIssueEmailModal: React.FC<SendIssueEmailModalProps> = ({
  issueCount,
  contacts,
  defaultRecipientIds,
  onConfirm,
  onClose,
}) => {
  const [selectedIds, setSelectedIds] = useState<string[]>(
    defaultRecipientIds.length > 0
      ? defaultRecipientIds
      : contacts.map((c) => c.id)
  );
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggleRecipient = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((r) => r !== id) : [...prev, id]
    );
  };

  const handleSend = async () => {
    if (selectedIds.length === 0) {
      setError('Select at least one recipient.');
      return;
    }
    setSending(true);
    setError(null);
    try {
      await onConfirm(selectedIds);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send email');
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="flex items-center justify-between p-5 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
              <Mail className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">
                Send Issue Report
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {issueCount} issue{issueCount !== 1 ? 's' : ''} will be emailed
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {error && (
            <div className="flex items-center gap-2 p-3 rounded-xl text-sm bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800/40">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {error}
            </div>
          )}

          <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-4 border border-amber-200 dark:border-amber-800/40">
            <p className="text-sm text-amber-800 dark:text-amber-300">
              Selected issues will be sent as an Excel attachment. After sending, these issues will be marked as "Under Process".
            </p>
          </div>

          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              <Users className="h-4 w-4" />
              Select Recipients
            </label>
            {contacts.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                No worker contacts configured. Add contacts in Settings.
              </p>
            ) : (
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {contacts.map((c) => {
                  const checked = selectedIds.includes(c.id);
                  return (
                    <label
                      key={c.id}
                      className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${
                        checked
                          ? 'border-amber-300 dark:border-amber-700 bg-amber-50/50 dark:bg-amber-900/10'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleRecipient(c.id)}
                        className="h-4 w-4 text-amber-500 rounded border-gray-300 dark:border-gray-600 focus:ring-amber-500"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                          {c.name}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                          {c.email}
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 p-5 border-t border-gray-200 dark:border-gray-700">
          <Button variant="secondary" onClick={onClose} disabled={sending}>
            Cancel
          </Button>
          <Button
            onClick={handleSend}
            disabled={sending || selectedIds.length === 0}
            loading={sending}
          >
            {sending ? (
              'Sending...'
            ) : (
              <>
                <Mail className="h-4 w-4 mr-1.5" />
                Send Email
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};
